import type { ComputedRef, Ref, WatchStopHandle } from 'vue';
import { computed, ref, unref, watch, watchEffect } from 'vue';
import type { MaybeRef } from '@vueuse/core';
import { isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { useRoute } from 'vue-router';
import useRouteChecks from './useRouteChecks';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import type { AnalyticsTrackTypeConstants } from '@/types/AnalyticsTrackTypeConstants';
import { createDate } from '@/utils/dateUtils';
import usePeriod from './usePeriod';
import useAnalysisModes from './useAnalysisModes';
import { ActiveReturnMode } from '@/types/ActiveReturnMode';
import { PeriodModule } from '@/store/modules/PeriodStore';
import useTranslation from './useTranslation';
import useStrategyMask from './useStrategyMask';
import { useGetDiscoverIndexAndType } from '@/composables/useGetDiscoverUtils';
import type { DSBPortfolioItemResponseDTO, DSBStrategyItemResponseDTO } from '@/api-v2/web/discover';
import { useIndexFxConversionName } from './useFxConversion';
import ChartDefaults from '@/constants/ChartDefaults';

const DEBUG = false;

/**
 * Use this composable to have a global state benchmark variable that updates whenever the item under analysis changes
 *
 * The benchmark variables may return a value, `null`, or `undefined`, which mean the following:
 * - `null` implies that the item purposefully has no benchmark set
 * - `undefined` implies that the benchmark for the item has not yet been loaded
 *
 * This will help us prevent making unnecessary API calls.
 *
 * Generally speaking, we want to supply `fallbackBenchmarkCode` when we're on Analytics pages.
 * For factsheets, we do NOT want to show a benchmark unless the user has defined one.
 */
export function useBenchmark({
  fallbackBenchmarkCode,
  isBenchmarkRequired,
  shouldResetDraftBenchmarkCodeOnSavedBenchmarkCodeChange,
  debug,
}: {
  /**
   * `fallbackBenchmarkCode` is designed to be used as default benchmark for a given scenario, which may differ
   * on a graph by graph basis. For example, we use ChartDefaults.DEFAULT_BENCHMARKS.BENCHMARK_3 as the benchmark
   * for our Rolling Charts, but we use ChartDefaults.DEFAULT_BENCHMARKS.BENCHMARK_2 as the yBenchmark for the Correlation
   * Chart.
   */
  fallbackBenchmarkCode?: MaybeRef<string | undefined>;
  isBenchmarkRequired?: MaybeRef<boolean | undefined>;
  /**
   * Because the `draftBenchmarkCode` is a global state variable, we only want to include a watcher to reset it
   * once per page.
   */
  shouldResetDraftBenchmarkCodeOnSavedBenchmarkCodeChange?: boolean;
  debug?: boolean;
} = {}): {
  /**
   * This cascading variable should be used most of the time when referring to a benchmark in the codebase.
   *
   * The cascading logic is as follows:
   * 1. if `localBenchmarkCode` is not null, return it
   * 2. else if `draftBenchmarkCode` (overridden by the user on a global basis) is not null, return it
   * 3. else if `savedBenchmarkCode` is not null, return it
   *    - if the user has saved a custom benchmark for the strategy or portfolio, we return it
   *    - otherwise we return the internal benchmark
   * 4. else if `fallbackBenchmarkCode` is not null, return it
   *
   * `undefined` - this variable may be undefined if we have not yet loaded whether there is a saved benchmark
   *   by the user or the organization.
   *
   * `null` - this variable is `null` when there is no benchmark.
   *
   * IMPORTANT: Please note that this code is NOT the identifier that is used for pinning the benchmark
   * in the colorMap. For that, you should use `benchmarkColorMapId` from `useGetColorMapIds`.
   */
  benchmarkCode: Ref<string | null | undefined>;

  /**
   * Returns the type of the draft benchmark if it exists, otherwise the type of the saved one
   * (if the user has access to it), else it returns the type of the fallback benchmark.
   */
  benchmarkType: Ref<AnalyticsTrackTypeConstants | null>;

  /**
   * `null` means that there is no benchmark.
   *
   * `undefined` means that there was a problem fetching the entity associated with the given `benchmarkCode`
   */
  benchmark: Ref<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;

  /**
   * Some of our graphs are initialized to use the index's benchmark as a comparison, but
   * we may want to override this in some graphs for other comparison. This variable
   * should not affect any other graphs, so it's just local to the graph in question.
   *
   * Please note that if this variable is truthy, it will ALWAYS override `draftBenchmarkCode` and `savedBenchmarkCode`.
   */
  localBenchmarkCode: Ref<string | null>;
  localBenchmarkType: Ref<AnalyticsTrackTypeConstants | null>;
  localBenchmark: Ref<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;

  /**
   * If there is an error getting the benchmark, this boolean will return true
   */
  isBenchmarkError: Ref<boolean>;
  benchmarkName: Ref<string>;
} {
  const {
    savedBenchmarkCode,
    savedBenchmark,

    draftBenchmarkCode,
    draftBenchmark,
  } = useItemUnderAnalysisGlobalState(debug);

  if (DEBUG || debug) {
    // eslint-disable-next-line no-restricted-syntax
    watch(draftBenchmarkCode, (newVal) => {
      console.log('draftBenchmarkCode changed to', newVal);
    });
  }

  if (DEBUG || debug) {
    // eslint-disable-next-line no-restricted-syntax
    watch(savedBenchmarkCode, (newVal) => {
      console.log(`========= savedBenchmarkCode changed to ${newVal} =========`);
    });
  }

  if (DEBUG || debug) {
    // eslint-disable-next-line no-restricted-syntax
    watch(savedBenchmark, (newVal) => {
      console.log(`========= savedBenchmark changed to`, newVal);
    });
  }

  const localBenchmarkCode = ref<string | null>(null);
  const { index: localBenchmark, type: localBenchmarkType } = useGetDiscoverIndexAndType(localBenchmarkCode);
  if (DEBUG || debug) {
    // eslint-disable-next-line no-restricted-syntax
    watch(localBenchmarkCode, (newVal) => {
      console.log('localBenchmarkCode changed to', newVal);
    });
  }

  const benchmarkCode = computed(() => {
    if (DEBUG || debug) {
      console.log('benchmarkCode re-running', {
        draftBenchmark: draftBenchmark.value,
        savedBenchmark: savedBenchmark.value,
        draftBenchmarkCode: draftBenchmarkCode.value,
        savedBenchmarkCode: savedBenchmarkCode.value,
      });
    }

    if (localBenchmark.value) {
      const retval = localBenchmarkCode.value;
      if (DEBUG || debug) console.log('benchmarkCode returning localBenchmarkCode', retval);

      return retval;
    }

    /**
     * If `draftBenchmark` is:
     * - truthy, we return `draftBenchmarkCode` because we have found the item corresponding to the `draftBenchmarkCode`
     * - `undefined`, we still return `draftBenchmarkCode` so that the user understands that there was a problem
     *     fetching the item corresponding to `draftBenchmarkCode`
     * - `null`, we fall through to the next condition
     */
    if (draftBenchmark.value !== null) {
      if (DEBUG || debug) console.log('benchmarkCode returning draftBenchmarkCode', draftBenchmarkCode.value);

      return draftBenchmarkCode.value;
    }

    /**
     * If `savedBenchmark` is:
     * - truthy, we return `savedBenchmarkCode` because we have found the item corresponding to the `savedBenchmarkCode`
     * - `undefined`, we still return `savedBenchmarkCode` so that the user understands that there was a problem
     *     fetching the item corresponding to `savedBenchmarkCode`
     * - `null`, we fall through to the next condition
     */
    if (savedBenchmark.value !== null) {
      if (DEBUG || debug) console.log('benchmarkCode returning savedBenchmarkCode', savedBenchmarkCode.value);

      return savedBenchmarkCode.value;
    }

    /**
     * If `fallbackBenchmarkCode` is truthy and `isBenchmarkRequired` is truthy, we return the `fallbackBenchmarkCode`
     * because for certain graphs we must show a benchmark, even if the user has not selected one.
     */
    if (unref(fallbackBenchmarkCode) && unref(isBenchmarkRequired)) {
      if (debug || DEBUG) console.log('benchmarkCode returning fallbackBenchmarkCode', unref(fallbackBenchmarkCode));

      return unref(fallbackBenchmarkCode);
    }

    if (debug || DEBUG) console.log('benchmarkCode returning null');

    /**
     * By default, no benchmark is set
     */
    return null;
  });
  if (DEBUG || debug) {
    // eslint-disable-next-line no-restricted-syntax
    watch(benchmarkCode, (newVal) => {
      console.log('benchmarkCode changed to', newVal);
    });
  }
  const { index: benchmark, type: benchmarkType } = useGetDiscoverIndexAndType(benchmarkCode);

  const isBenchmarkError = computed(() => {
    if (!!benchmarkCode.value && benchmark.value == null) return true;

    return false;
  });

  const { translate } = useTranslation();
  const { getStrategyMask } = useStrategyMask();

  const benchmarkName = computed(() => {
    if (!benchmark.value)
      return translate({
        path: 'FACTSHEETS.PERFORMANCE_SUMMARY.BENCHMARK_UNAVAILABLE',
      });

    if ('code' in benchmark.value) return getStrategyMask(benchmark.value);

    return benchmark.value.name;
  });

  // eslint-disable-next-line no-restricted-syntax
  watch([draftBenchmarkCode, savedBenchmarkCode], () => {
    if (DEBUG || debug) console.log('localBenchmarkCode reset to null');
    localBenchmarkCode.value = null;
  });

  if (shouldResetDraftBenchmarkCodeOnSavedBenchmarkCodeChange) {
    // eslint-disable-next-line no-restricted-syntax
    watch(savedBenchmarkCode, () => {
      if (DEBUG || debug) console.log('draftBenchmarkCode reset to null');
      draftBenchmarkCode.value = null;
    });
  }

  return {
    benchmarkCode,
    benchmarkType,
    benchmark,

    localBenchmarkCode,
    localBenchmarkType,
    localBenchmark,

    isBenchmarkError,
    benchmarkName,
  };
}

export const useUpdatePeriodOnBenchmarkChange = (): WatchStopHandle => {
  const { disabledBeforeDate } = usePeriod();
  const { activeReturnMode } = useAnalysisModes();
  const { itemUnderAnalysis } = useItemUnderAnalysisGlobalState();
  const { benchmark, benchmarkCode } = useBenchmark();

  const route = useRoute();
  const { isOnPerformance, isOnCorrelation, isOnFactorDecomposition } = useRouteChecks(route);

  const shouldBenchmarkAffectAnalysisPeriod = computed(() => {
    if (isOnPerformance.value || isOnFactorDecomposition.value) {
      return activeReturnMode.value === ActiveReturnMode.ACTIVE_RETURN && benchmarkCode.value !== null;
    }

    if (isOnCorrelation.value) {
      return benchmarkCode.value !== null;
    }

    return false;
  });

  // eslint-disable-next-line no-restricted-syntax
  return watch(
    [shouldBenchmarkAffectAnalysisPeriod, benchmark],
    ([newShouldBenchmarkAffectAnalysisPeriod, newBenchmark]) => {
      if (DEBUG)
        console.log('shouldBenchmarkAffectAnalysisPeriod or benchmark changed', {
          newShouldBenchmarkAffectAnalysisPeriod,
          newBenchmark,
        });

      if (!newBenchmark) {
        console.error('Has benchmark code but we somehow do not have the actual data for the benchmark');
        return;
      }

      const benchmarkStartDate = newBenchmark.historyStartDate;

      if (!benchmarkStartDate) {
        console.error('Has data for the benchmark but historyStartDate is missing!');
        return;
      }

      if (
        !itemUnderAnalysis.value ||
        !isPortfolioTreeSubportfolio(itemUnderAnalysis.value) ||
        !itemUnderAnalysis.value.historyStartDate
      )
        return;

      // when benchmark value does not affect analysis period, we set the disabledBeforeDate to the itemUnderAnalysis's historyStartDate
      if (!newShouldBenchmarkAffectAnalysisPeriod) {
        PeriodModule.SetDisabledBeforeAfterDates({
          beforeDate: itemUnderAnalysis.value.historyStartDate,
        });
        return;
      }

      // if benchmark value affects analysis period, we use the closest date as disabledBeforeDate
      if (createDate(benchmarkStartDate) > createDate(disabledBeforeDate.value)) {
        PeriodModule.SetDisabledBeforeAfterDates({
          beforeDate: benchmarkStartDate,
          triggerWarning: true,
        });
      }
    },
  );
};

/**
 * Designed to be used for cases when a draft benchmark is required.
 *
 * Currently used in the Active Return dropdown and the Portfolio Construction toolbar on the Correlation tab.
 */
export function useGetRequiredDraftBenchmarkUtilities(
  { fallbackBenchmarkCode }: { fallbackBenchmarkCode: string } = {
    fallbackBenchmarkCode: ChartDefaults.DEFAULT_BENCHMARKS.BENCHMARK_3,
  },
): {
  /**
   * Variable to hold the benchmark code before we propagate changes to `draftBenchmarkCode`.
   *
   * We may not use `localBenchmarkCode` because `localBenchmarkCode` trumps `draftBenchmarkCode` and `savedBenchmarkCode`
   * in all cases if it is truthy.
   *
   * NOTE: We use this variable instead of `localBenchmarkCode` because we need to propagate the
   * `fallbackBenchmarkCode` value to `draftBenchmarkCode` ONLY in some special cases.
   */
  userSelectedBenchmarkCode: Ref<string | null>;

  /**
   * Generally speaking, we should reset the user selected benchmark code when the rootItemUnderAnalysis changes.
   *
   * Returning this util function for the caller to handle the reset as appropriate.
   */
  resetUserSelectedBenchmarkCode: () => void;

  /**
   * In the special cases mentioned above, we must always show a benchmark, even if the user has not selected one.
   *
   * For example, this variable is the one bound to the `ActiveReturnDropdown -> ComparisonTrackControl`,
   * but `userSelectedBenchmarkCode` is the variable that is used to store the value that the user has selected.
   */
  shownBenchmarkCode: Ref<string>;

  shownBenchmark: ComputedRef<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;

  shownBenchmarkNameFxConversion: ComputedRef<string>;

  /**
   * If the user has selected a benchmark, we return it directly.
   *
   * If the user has not selected a benchmark, we must handle some special cases:
   * - If a benchmark is required (according to `shouldUseFallback`), we return the fallback benchmark
   * - If a benchmark is NOT required, and we are currently using the fallback benchmark,
   *     we return null to reset `draftBenchmarkCode`
   *
   * Note: if the savedBenchmarkCode is undefined, we return undefined to indicate that we haven't finished loading.
   */
  getStateDependentDraftBenchmarkFallback: ({
    shouldUseFallback,
    debug,
  }: {
    shouldUseFallback: boolean;
    debug?: boolean;
  }) => string | null | undefined;

  /**
   * In cases when we must conditionally use a benchmark but the user has not selected one,
   * we must update the draft benchmark code to the appropriate value.
   *
   * This watcher will also update the draft benchmark code if the user updates the saved benchmark.
   */
  useUpdateDraftBenchmarkOnRequiredBenchmarkStateChange: ({
    isBenchmarkRequired,
    shouldWatchUserSelectedBenchmarkCode,
    shouldWatchImmediate,
  }: {
    isBenchmarkRequired: Ref<boolean>;

    /**
     * For instances in which we want to automatically apply changes from `userSelectedBenchmarkCode` to `draftBenchmarkCode`,
     * this should be marked as `true`. For example, when we change the benchmark on the Correlation tab,
     * we want to immediately apply changes after the modal is closed.
     *
     * However, for the Active Return dropdown, we only want to apply changes when the user clicks the Apply button.
     * So for that case, we should mark this as `false`.
     */
    shouldWatchUserSelectedBenchmarkCode: boolean;

    /**
     * We purposefully only use `shouldWatchImmediate` for the Portfolio Construction toolbar on the Correlation tab
     * because currently that is the only case in which we may require using a benchmark on page load.
     *
     * We do not use `shouldWatchImmediate` for the Active Return dropdown because Active Return is not enabled
     * on page load.
     */
    shouldWatchImmediate: boolean;
  }) => WatchStopHandle;
} {
  const userSelectedBenchmarkCode = ref<string | null>(null);

  const resetUserSelectedBenchmarkCode = (): void => {
    userSelectedBenchmarkCode.value = null;
  };

  const { benchmarkCode } = useBenchmark();
  const { savedBenchmarkCode, draftBenchmarkCode } = useItemUnderAnalysisGlobalState();

  const shownBenchmarkCode = computed(
    (): string => userSelectedBenchmarkCode.value ?? benchmarkCode.value ?? fallbackBenchmarkCode,
  );
  const { index: shownBenchmark, type: shownBenchmarkType } = useGetDiscoverIndexAndType(shownBenchmarkCode);

  /**
   * This variable is used to determine if we are currently using the fallback benchmark.
   *
   * This allows us to differentiate between the user having not selected a benchmark, and the user having selected the fallback benchmark.
   */
  const isUsingFallbackBenchmark = computed(
    (): boolean => userSelectedBenchmarkCode.value === null && shownBenchmarkCode.value === fallbackBenchmarkCode,
  );

  const getStateDependentDraftBenchmarkFallback = ({
    shouldUseFallback,
    debug,
  }: {
    shouldUseFallback: boolean;
    debug?: boolean;
  }): string | null | undefined => {
    if (debug || DEBUG)
      console.log('getStateDependentDraftBenchmarkFallback re-running', {
        shouldUseFallback,
        userSelectedBenchmarkCode: userSelectedBenchmarkCode.value,
        benchmarkCode: benchmarkCode.value,
        isUsingFallbackBenchmark: isUsingFallbackBenchmark.value,
      });

    if (savedBenchmarkCode.value === undefined) {
      // savedBenchmark is undefined, so we haven't finished loading, so we do nothing
      return;
    }

    // if the user has not selected a benchmark, and we don't have a saved benchmark, we must handle some special cases
    if (!userSelectedBenchmarkCode.value && !savedBenchmarkCode.value) {
      if (shouldUseFallback) {
        if (DEBUG || debug)
          console.log('getStateDependentDraftBenchmarkFallback returning fallbackBenchmarkCode', fallbackBenchmarkCode);

        return fallbackBenchmarkCode;
      }

      // if the user has not selected a benchmark and we are currently using the fallback benchmark,
      // return null to reset `draftBenchmarkCode` (thus also resetting `userSelectedBenchmarkCode`)
      if (isUsingFallbackBenchmark.value) {
        if (DEBUG || debug)
          console.log(
            'getStateDependentDraftBenchmarkFallback returning null because isUsingFallbackBenchmark is true',
          );

        return null;
      }
    }

    if (DEBUG || debug)
      console.log(
        'getStateDependentDraftBenchmarkFallback returning userSelectedBenchmarkCode',
        userSelectedBenchmarkCode.value,
      );

    return userSelectedBenchmarkCode.value ?? benchmarkCode.value ?? null;
  };

  const shownBenchmarkNameFxConversion = useIndexFxConversionName(
    computed(() => ({
      codeOrSlug: shownBenchmarkCode.value,
      type: shownBenchmarkType.value,
    })),
  );

  watchEffect(() => {
    // when we reset the draft benchmark code, we also want to reset the user selected benchmark code
    // so we don't retain the old draftBenchmarkCode value
    if (draftBenchmarkCode.value === null) {
      resetUserSelectedBenchmarkCode();
    }
  });

  const useUpdateDraftBenchmarkOnRequiredBenchmarkStateChange = ({
    isBenchmarkRequired,
    shouldWatchUserSelectedBenchmarkCode,
    shouldWatchImmediate,
  }: {
    isBenchmarkRequired: Ref<boolean>;
    shouldWatchUserSelectedBenchmarkCode: boolean;
    shouldWatchImmediate: boolean;
  }): WatchStopHandle => {
    const itemsToWatch: [Ref<boolean>, Ref<unknown>] = [isBenchmarkRequired, savedBenchmarkCode];
    if (shouldWatchUserSelectedBenchmarkCode) {
      itemsToWatch.push(userSelectedBenchmarkCode);
    }

    // eslint-disable-next-line no-restricted-syntax
    return watch(
      itemsToWatch,
      ([newIsBenchmarkRequired]) => {
        const stateDependentDraftBenchmarkFallback = getStateDependentDraftBenchmarkFallback({
          shouldUseFallback: newIsBenchmarkRequired,
        });
        if (stateDependentDraftBenchmarkFallback === undefined) {
          // benchmarkToUse is undefined, so we haven't finished loading, so we do nothing
          return;
        }

        // if the benchmark to use is the same as the current benchmark,
        // we don't need to update the draft benchmark code
        if (stateDependentDraftBenchmarkFallback === benchmarkCode.value) {
          return;
        }

        draftBenchmarkCode.value = stateDependentDraftBenchmarkFallback;
      },
      { immediate: shouldWatchImmediate },
    );
  };

  return {
    userSelectedBenchmarkCode,
    resetUserSelectedBenchmarkCode,
    getStateDependentDraftBenchmarkFallback,
    shownBenchmark,
    shownBenchmarkCode,
    shownBenchmarkNameFxConversion,
    useUpdateDraftBenchmarkOnRequiredBenchmarkStateChange,
  };
}
